import { PlayerTask } from './PlayerTasks';
import IPlayerSocialData, { IFriendData } from 'Player/PlayerProperty/Social/IPlayerSocialData';
import { QuestType } from './QuestType';
import IPlayerInventoryData from 'Player/Inventory/IPlayerInventoryData';
import IPlayerProgressData from 'Player/PlayerProperty/Progress/IPlayerProgressData';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import IPlayerSettingsData from 'Player/PlayerProperty/Settings/IPlayerSettingsData';
import { tPlayerLevelMap } from './Progress/PlayerLevels';
import { FBTrackingEvent } from 'Utility/Tracking/FBTrackingEvent';
import { ILuckyTurnipsState } from '../LuckyTurnips/ILuckyTurnipsState';
import { IPetsState } from './Pets/IPetsState';
import { AnyBoosterType } from '../Card/BoosterType';
import { IFarmPlayerState } from './Farm/IFarmPlayerState';
import { EventType } from 'Utility/Helpers/Events/EventType';
import { ProductId } from 'Utility/Product';
import { FarmItemId } from 'System/Farm/Data/FarmItemData';
import { OfferTier } from 'Utility/Helpers/IAP/Offers/Offers';
import { IPlayerSeasonPass } from 'Types/State/Player/SeasonPass';

export type IPlayerStateQuestData = IBasePlayerQuestData | IPlayerExpireableQuestData;

export type PlayerFTUX = FTUXType | EventType;
export interface IBasePlayerQuestData {
    id: string;
    type: QuestType;
    cooldownStartedAt: Date | null;
    progress?: number;
    tier?: number;
    indexOfLastRewardCollected?: number;
    eventType?: EventType;
}

export interface IPlayerExpireableQuestData extends IBasePlayerQuestData {
    expiration: Date | null;
}

export interface IPlayerStateTaskData {
    task?: PlayerTask;

    /** Should the task be played before any reward tasks */
    prePlayTask?: boolean;

    [key: string]: any;
}

export interface IPlayerUnlock {
    type: UnlockType;
    id?: string;
}

export type PlayerUnlock = IPlayerUnlock;

// FIXME: clean up
export enum UnlockType {
    SUITCASE = 'suitcase',
    BOOSTER_TNT = 'booster_tnt',
    BOOSTER_BULLS_EYE = 'booster_bulls_eye',
    BOOSTER_SHUFFLE = 'booster_shuffle',

    // TODO: Persist in player save data.
    FREE_JOKER_BOOSTERS = 'free_jokers_boosters',

    // TODO: Persist in player save data.
    FREE_UNDO_BOOSTERS = 'free_jokers_boosters',

    SEASON_PASS = 'season_pass'
}

export interface IPlayerFlag {
    id: string;
    date: string;
    expiryDate?: string;
}

export interface IPlayerState {
    inventory: IPlayerInventoryData;
    progress: IPlayerProgressData;
    settings: IPlayerSettingsData;
    social: IPlayerSocialData;
    timers: Map<string, Date | null>;
    unlockedBoosters: AnyBoosterType[];

    petsState: IPetsState;

    farmState: IFarmPlayerState;

    // Progress
    levels: tPlayerLevelMap;
    fields: string[];
    quests: IPlayerStateQuestData[];
    eventQuests: IPlayerExpireableQuestData[];
    /** The level of the currently active star chest, if there is one */
    starChestLevel: number | null;
    /** The level that the pet should be active on */
    petLevel: number | null;
    luckyTurnipsState: ILuckyTurnipsState | null;
    maxWorld: number;
    FTUX: PlayerFTUX[];
    // Last wheel claimed
    lastClaimedSpinLevel: number;

    // If player had spun first time the wheels
    spunFirstNormalWheel: boolean;
    spunFirstDiamondWheel: boolean;

    // Current State
    tasks: IPlayerStateTaskData[];

    // Events
    FBEvents: FBTrackingEvent[];

    // To save the if FB was unified with mobile
    isFbInstantUnified: boolean;

    // Tracking
    usedCheats: boolean;
    purchases: number;
    realMoneySpent: number;
    coinValueBoughtWithRealMoney: number;
    sessionCount: number;
    totalSessionCount: number;
    botOptIn: boolean;
    hasShortcut: boolean;
    lastSundayOfferPurchaseTimestamp: number | undefined;
    attributionCampaignID: string | undefined; // SOFA-1927
    premiumWheelResetLevel: number;
    premiumWheelSpinsAmount: number;
    gdprCompliance: boolean;
    productsPurchased: PurchasedProducts;
    decoItemsRewarded: FarmItemId[];
    decoItemsSeen: FarmItemId[];
    deviceId: string;
    facebookUserId: string;

    /**
     * Timestamp used to determine mission attempts within first 24 hours for new/existing players (starting when tracking build is released).
     * This data is used for user acquisition cohort training.
     */
    missionAttemptsStartTimestamp: Date;

    /**
     * Number of mission attempts within first 24 hours of playing.
     */
    missionAttempts: number;

    /**
     * Number of plus-five booster usages to be used for FTUX/tutorial purposes.
     * Currently only used for controlling results of first and second usages of +5 cards booster.
     */
    plusFiveBoostersUsed: number;

    /**
     * Persisted game and associated user IDs for the player collected through x-promo entrypoints. This must be persisted as there
     */
    otherGameIds: IPlayerGameId[];

    /**
     * Persisted offer data used to keep player in the same tier after offer is shown.
     */
    currentEventOffer?: IEventOffer;

    /**
     * Lost data string that will be parsed and added as a data conflict
     */
    __lostData?: string;

    /**
     * Store some flags and when they were added.
     * Should be mainly used for events.
     */
    flags: IPlayerFlag[];

    playerUnlocks: PlayerUnlock[];

    seasonPass: IPlayerSeasonPass;

    hasResetVolume: boolean;
}

export interface IPlayerSocialState {
    receivedMessages: string[];
    friendData: IFriendData[];
    friendCount: number;
}

export type PurchasedProducts = {
    [key in ProductId]?: number;
};

// TODO: Find a better place for these types/interfaces.
export interface IPlayerGameId {
    gameId: string;
    playerId: string;
}

export interface IEventOffer {
    type: EventType;
    tier: OfferTier;
}

export interface IPlayerFlag {
    id: string;
    date: string;
    expiryDate?: string;
}